<template>
  <div id="simple-calendar-app" class="pb-12">
    <div class="vx-card p-4 overflow-x-auto">
      
      <!-- =========================================================================================
          CUSTOM CALENDAR HEADER
      ========================================================================================== -->
      <div class="p-6 border-b border-solid border-grey-light mb-4">
        <div class="vx-row no-gutter flex-wrap items-center">

          <!-- View Selection Buttons (Week, Month, Year) -->
          <div class="vx-col w-full sm:w-1/3 mb-4 sm:mb-0 flex justify-center sm:justify-start items-center">
            <template v-for="(view, index) in calendarViewTypes">
              <vs-button
                  v-if="calendarView === view.val"
                  :key="String(view.val) + 'filled'"
                  type="filled"
                  class="p-3 md:px-6 md:py-2 text-sm"
                  :class="{'border-l-0 rounded-l-none': index, 'rounded-r-none': calendarViewTypes.length !== index+1}"
                  @click="calendarView = view.val"
              >{{ view.label }}</vs-button>
              <vs-button
                  v-else
                  :key="String(view.val) + 'border'"
                  type="border"
                  class="p-3 md:px-6 md:py-2 text-sm"
                  :class="{'border-l-0 rounded-l-none': index, 'rounded-r-none': calendarViewTypes.length !== index+1}"
                  @click="calendarView = view.val"
              >{{ view.label }}</vs-button>
            </template>
          </div>

          <!-- Status Indicators (Legend) -->
          <div class="vx-col w-full sm:w-1/3 mb-4 sm:mb-0 flex justify-center items-center">
            <feather-icon icon="CircleIcon" title='Skill not added' :svgClasses="['fill-current h-4 w-4', 'text-danger']" />
            <feather-icon icon="CircleIcon" title='Start working on' :svgClasses="['fill-current h-4 w-4', 'text-primary']" class="ml-4"/>
            <feather-icon icon="CircleIcon" title='Goal Achieved' :svgClasses="['fill-current h-4 w-4', 'text-success']" class="ml-4"/>
          </div>

          <!-- Date Navigation Range display -->
          <div class="vx-col w-full sm:w-1/3 flex justify-center sm:justify-end items-center">
            <feather-icon
                :icon="$vs.rtl ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
                @click="updateMonth(-1)"
                svgClasses="w-6 h-6 m-1 p-1"
                class="cursor-pointer bg-primary text-white rounded-full hover:bg-primary-dark transition-colors" />

            <!-- Dynamically formatted date string -->
            <span class="mx-4 text-lg font-semibold whitespace-no-wrap text-dark">
              {{ formattedHeaderDate }}
            </span>

            <feather-icon
                :icon="$vs.rtl ? 'ChevronLeftIcon' : 'ChevronRightIcon'"
                @click="updateMonth(1)"
                svgClasses="w-6 h-6 m-1 p-1"
                class="cursor-pointer bg-primary text-white rounded-full hover:bg-primary-dark transition-colors" />
          </div>

        </div>
      </div>

      <!-- =========================================================================================
          NATIVE CALENDAR VIEW (Used for Month & Week views)
      ========================================================================================== -->
      <calendar-view
          v-show="calendarView !== 'year'"
          ref="calendar"
          :displayPeriodUom="calendarView"
          :show-date="showDate"
          :events="eventsData"
          enableDragDrop
          :eventTop="windowWidth <= 400 ? '2rem' : '3rem'"
          eventBorderHeight="0px"
          eventContentHeight="1.65rem"
          class="theme-default custom-calendar-wrapper"
          @drop-on-date="eventDragged">
        
        <!-- Declaring empty slot to suppress native header -->
        <div slot="header"></div>
      </calendar-view>

      <!-- =========================================================================================
          CUSTOM 4x3 MONTH MATRIX (Used for Year view)
      ========================================================================================== -->
      <div v-show="calendarView === 'year'" class="year-view-matrix px-2 pb-6">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full sm:w-1/3 md:w-1/4 p-2" v-for="month in months" :key="month.value">
            <div @click="selectMonth(month.value)"
                 class="py-8 text-center cursor-pointer rounded-lg border border-solid transition-all duration-200"
                 :class="{
                   'bg-primary text-white shadow-lg border-primary font-bold scale-105': isCurrentMonth(month.value),
                   'bg-white text-dark border-grey-light hover:bg-grey-light hover:shadow-md': !isCurrentMonth(month.value)
                 }">
              <span class="text-xl">{{ month.name }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { CalendarView, CalendarViewHeader } from 'vue-simple-calendar'
import moduleCalendar from '@/store/calendar/moduleCalendar.js'
require('vue-simple-calendar/static/css/default.css')

import Datepicker from 'vuejs-datepicker'
import { en, he } from 'vuejs-datepicker/src/locale'

import baseData from "@/i18n/i18n-en-Data-DPM.js";           
import deepData from "@/i18n/i18n-en-deep-Data.js";       
import pciData from "@/i18n/i18n-en-pci-Data-Coach.js";         
import peopletreeData from "@/i18n/i18n-en-peopletree-Data.js"; 

export default {
  components: {
    CalendarView,
    CalendarViewHeader,
    Datepicker
  },
  data () {
    return {
      showDate: new Date(),
      disabledFrom: false,
      CCD: this.$t("addNewSkill.data"),
      id: 0,
      title: '',
      startDate: '',
      endDate: '',
      labelLocal: 'none',

      langHe: he,
      langEn: en,
      eventsData: [],

      url: '',
      // Set default to week based on your screenshot requirement
      calendarView: 'week', 

      activePromptAddEvent: false,
      activePromptEditEvent: false,

      calendarViewTypes: [
        { label: 'Week', val: 'week' },
        { label: 'Month', val: 'month' },
        { label: 'Year', val: 'year' }
      ]
    }
  },
  computed: {
    // Replaces the broken `| month` filter in the template
    formattedHeaderDate() {
      if (!this.showDate) return '';
      
      if (this.calendarView === 'year') {
        return this.showDate.getFullYear();
      } else {
        // Formats as "Oct 2026"
        return this.showDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    },
    simpleCalendarEvents () {
      return this.$store.state.calendar.events
    },
    validForm () {
      return this.title !== '' && this.startDate !== '' && this.endDate !== '' && Date.parse(this.endDate) - Date.parse(this.startDate) >= 0 && !this.errors.has('event-url')
    },
    disabledDatesTo () {
      return { to: new Date(this.startDate) }
    },
    disabledDatesFrom () {
      return { from: new Date(this.endDate) }
    },
    calendarLabels () {
      return this.$store.state.calendar.eventLabels
    },
    labelColor () {
      return (label) => {
        if      (label === 'business') return 'success'
        else if (label === 'work')     return 'warning'
        else if (label === 'personal') return 'danger'
        else if (label === 'none')     return 'primary'
      }
    },
    windowWidth () {
      return this.$store.state.windowWidth
    },
    months () {
      return [
        { name: 'January', value: 0 },
        { name: 'February', value: 1 },
        { name: 'March', value: 2 },
        { name: 'April', value: 3 },
        { name: 'May', value: 4 },
        { name: 'June', value: 5 },
        { name: 'July', value: 6 },
        { name: 'August', value: 7 },
        { name: 'September', value: 8 },
        { name: 'October', value: 9 },
        { name: 'November', value: 10 },
        { name: 'December', value: 11 }
      ]
    }
  },
  methods: {
    addEvent () {
      const obj = { title: this.title, startDate: this.startDate, endDate: this.endDate, label: this.labelLocal, url: this.url }
      obj.classes = `event-${  this.labelColor(this.labelLocal)}`
      this.$store.dispatch('calendar/addEvent', obj)
    },
    getFrameworkData(user) {
      const type = user.framework || user.program || 'founder'; 
      
      if (['founder', 'pci'].includes(type.toLowerCase())) return pciData.addNewSkill.data;
      if (['behavioural', 'dpm'].includes(type.toLowerCase())) return baseData.addNewSkill.data;
      if (['ecosystem', 'deep'].includes(type.toLowerCase())) return deepData.addNewSkill.data;
      if (['performance', 'peopletree'].includes(type.toLowerCase())) return peopletreeData.addNewSkill.data;
      
      return this.CCD; 
    },
    // FIXED Navigation Logic
    updateMonth (val) {
      const newDate = new Date(this.showDate.valueOf()); // Create a copy of current date
      
      if (this.calendarView === 'year') {
        newDate.setFullYear(newDate.getFullYear() + val);
      } else if (this.calendarView === 'month') {
        newDate.setMonth(newDate.getMonth() + val);
      } else if (this.calendarView === 'week') {
        // Shift by 7 days for week view
        newDate.setDate(newDate.getDate() + (val * 7)); 
      }
      
      this.showDate = newDate;
    },
    selectMonth (monthValue) {
      const newDate = new Date(this.showDate)
      newDate.setMonth(monthValue)
      this.showDate = newDate
      this.calendarView = 'month' 
    },
    isCurrentMonth (monthValue) {
      const today = new Date()
      return today.getFullYear() === this.showDate.getFullYear() && today.getMonth() === monthValue
    },
    clearFields () {
      this.title = this.endDate = this.url = ''
      this.id = 0
      this.labelLocal = 'none'
    },
    promptAddNewEvent (date) {
      this.disabledFrom = false
      this.addNewEventDialog(date)
    },
    addNewEventDialog (date) {
      this.clearFields()
      this.startDate = date
      this.endDate = date
      this.activePromptAddEvent = true
    },
    openAddNewEvent (date) {
      this.disabledFrom = true
      this.addNewEventDialog(date)
    },
    openEditEvent (event) {
      const e = this.$store.getters['calendar/getEvent'](event.id)
      this.id = e.id
      this.title = e.title
      this.startDate = e.startDate
      this.endDate = e.endDate
      this.url = e.url
      this.labelLocal = e.label
      this.activePromptEditEvent = true
    },
    editEvent () {
      const obj = { id: this.id, title: this.title, startDate: this.startDate, endDate: this.endDate, label: this.labelLocal, url: this.url }
      obj.classes = `event-${  this.labelColor(this.labelLocal)}`
      this.$store.dispatch('calendar/editEvent', obj)
    },
    removeEvent () {
      this.$store.dispatch('calendar/removeEvent', this.id)
    },
    eventDragged (event, date) {
      this.$store.dispatch('calendar/eventDragged', {event, date})
    }
  },
  created () {
    this.$store.registerModule('calendar', moduleCalendar)
    let traineeQuery={};
    if(window.$cookies.get('companyId')){
      traineeQuery = {
        status: { $ne: 'deleted' },
        userType: "trainee",
        companyId: window.$cookies.get('companyId')
      }
      this.$store
      .dispatch("userManagement/fetchUsers", {query: traineeQuery})
      .then(res => {
        const usersList = res.data ? res.data : (Array.isArray(res) ? res : []);
        
        if (usersList.length === 0) {
          this.eventsData = [];
          return; 
        }

        this.user_data = res;
        let eventData = [];
        let usersId = [];
        
        for (let i = 0; i < usersList.length; i++) {
          usersId.push(usersList[i].id);
        }

        this.$store
          .dispatch("trainee-competencies/find", { query: { usersId: { $in: usersId } } })
          .then(traineeCompetencies => {
            
            const compList = traineeCompetencies.data ? traineeCompetencies.data : (Array.isArray(traineeCompetencies) ? traineeCompetencies : []);
            let uniqueUsersId = [];
            
            for (let i = 0; i < usersList.length; i++) {
              for (let j = 0; j < compList.length; j++) {
                if(compList[j].usersId === usersList[i].id){
                  
                  const compId = compList[j].competencyId; 
                  const characteristicId = compId.split("-")[0]; 

                  const correctCCD = this.getFrameworkData(usersList[i]);
                  const category = correctCCD[characteristicId];
                  const competencies = category ? category.competencies : [];

                  const currentCompetency = competencies.find(c => c.id === compId);
                  this.currentCompetency = currentCompetency;
                  
                  let skillName = currentCompetency ? currentCompetency.comp : "Unknown Skill";

                  // FIX: Created strictly unique IDs for each event
                  const uniqueEventId = `skill_${usersList[i].id}_${compId}_${j}`;

                  if(compList[j].progressStatus === 'in_progress'){
                    eventData.push({
                      "title": usersList[i].firstName + " " + usersList[i].lastName + this.$t('calender.c1') + skillName + " .",
                      "startDate": compList[j].createdAt,
                      "endDate": compList[j].createdAt,
                      "label": "none",
                      "classes": "event-primary",
                      "url": "",
                      "id": uniqueEventId 
                    });
                  }
                  else if(compList[j].progressStatus === 'complete'){
                    eventData.push({
                      "title": this.$t('calender.c2') + skillName + this.$t('calender.c3') + usersList[i].firstName + " " + usersList[i].lastName + " .",
                      "startDate": compList[j].updatedAt,
                      "endDate": compList[j].updatedAt,
                      "label": "none",
                      "classes": "event-success",
                      "url": "",
                      "id": uniqueEventId 
                    });
                  }
                }
                else {
                  uniqueUsersId.push(compList[j].usersId);
                }
              }
            }

            let usersIdDiff = [...new Set(uniqueUsersId)];
            let result = usersId.filter(number => !usersIdDiff.includes(number));
            
            if (result.length === 0) {
               this.eventsData = [...new Set(eventData)];
               return;
            }
            
            this.$store
              .dispatch("userManagement/fetchUsers", {
                query: {
                  id: { $in: result },
                  status: { $ne: 'deleted' },
                  userType: "trainee",
                  companyId: window.$cookies.get('companyId')
                }
              })
              .then(userSkillNotAddedData => {
                const noSkillUsers = userSkillNotAddedData.data ? userSkillNotAddedData.data : (Array.isArray(userSkillNotAddedData) ? userSkillNotAddedData : []);
                let uinqueData = [];
                
                for (let i = 0; i < noSkillUsers.length; i++) {
                  uinqueData.push({
                    "title": noSkillUsers[i].firstName + " " + noSkillUsers[i].lastName + this.$t('calender.c4'),
                    "startDate": noSkillUsers[i].createdAt,
                    "endDate": noSkillUsers[i].createdAt,
                    "label": "none",
                    "classes": "event-danger",
                    "url": "",
                    "id": `no_skill_${noSkillUsers[i].id}` // FIX: Unique ID for no skill events
                  });
                }
                this.eventsData = [...new Set(eventData), ...new Set(uinqueData)];
              }).catch(err => console.error(err));
              
          }).catch(err => console.error(err));
      })
      // FIX: Cleaned up the malformed and duplicated catch block
      .catch(err => {
        if (err.response && err.response.status === 404) {
          console.warn("Users not found for calendar.");
          return;
        }
        console.error(err);
      });
    }
  },
  beforeDestroy () {
    this.$store.unregisterModule('calendar')
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";

#simple-calendar-app {
  .vx-card {
    height: auto !important; 
    overflow: visible !important;
  }
  
  .custom-calendar-wrapper {
    min-height: 600px; 
    margin-bottom: 1.5rem;
    
    /* Ensure events stack nicely in week/month view */
    .cv-event {
      white-space: normal;
      line-height: 1.2;
      padding: 4px;
      border-radius: 4px;
    }
  }

  .year-view-matrix {
    .hover\:bg-grey-light:hover {
      background-color: #f8f8f8;
    }
  }
}
</style>